/**
 * DividerContent - 可配置分隔线组件（支持多种样式）
 */

import React from 'react';
import './styles/divider-content.css';

export default function DividerContent({ data }) {
  if (!data) return null;

  const cfg = {
    style: data.style || 'line-dot',
    color: data.color || '#D4BFA0',
    thickness: Number(data.thickness || 4),
    lengthPercent: Math.min(100, Math.max(10, Number(data.lengthPercent || 88))),
    align: data.align || 'right',
    showTop: data.showTop !== false,
    showBottom: !!data.showBottom,
    // 装饰元素配置（兼容旧字段）
    decorationColor: data.decorationColor || data.dotColor || data.color || '#D4BFA0',
    decorationSize: data.decorationSize || data.dotSize || 8,
    // 渐变配置
    gradientFrom: data.gradientFrom || data.color || '#D4BFA0',
    gradientTo: data.gradientTo || data.color || '#E5D4B0',
    // 间距
    marginTop: Number(data.marginTop || 12),
    marginBottom: Number(data.marginBottom || 12)
  };

  // 判断是否支持装饰元素
  const supportsDecoration = ['line-dot', 'line-square', 'line-diamond', 'line-triangle'].includes(cfg.style);
  const supportsGradient = cfg.style === 'gradient';

  // 渲染装饰元素
  const renderDecoration = (position = 'right') => {
    if (!supportsDecoration) return null;

    const decorationStyle = {
      width: cfg.decorationSize,
      height: cfg.decorationSize,
      backgroundColor: cfg.decorationColor,
      flexShrink: 0,
    };

    // 根据样式类型设置不同的形状
    switch (cfg.style) {
      case 'line-dot':
        decorationStyle.borderRadius = cfg.decorationSize;
        break;
      case 'line-square':
        decorationStyle.borderRadius = 0;
        break;
      case 'line-diamond':
        decorationStyle.clipPath = 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';
        decorationStyle.backgroundColor = cfg.decorationColor;
        break;
      case 'line-triangle':
        decorationStyle.clipPath = 'polygon(50% 0%, 0% 100%, 100% 100%)';
        decorationStyle.backgroundColor = cfg.decorationColor;
        break;
      default:
        decorationStyle.borderRadius = cfg.decorationSize;
    }

    return <span className="divider-decoration" style={decorationStyle} />;
  };

  // 渲染线条
  const renderLine = () => {
    const lineStyle = {
      width: cfg.lengthPercent + '%',
      flexShrink: 0,
    };

    // 根据样式类型设置不同的线条样式
    switch (cfg.style) {
      case 'solid':
      case 'line-dot':
      case 'line-square':
      case 'line-diamond':
      case 'line-triangle':
        // 实线
        lineStyle.height = cfg.thickness;
        lineStyle.backgroundColor = cfg.color;
        lineStyle.borderRadius = cfg.thickness / 2;
        break;

      case 'dashed':
        // 虚线
        lineStyle.height = 0;
        lineStyle.borderTop = `${cfg.thickness}px dashed ${cfg.color}`;
        lineStyle.backgroundColor = 'transparent';
        break;

      case 'dotted':
        // 点线
        lineStyle.height = 0;
        lineStyle.borderTop = `${cfg.thickness}px dotted ${cfg.color}`;
        lineStyle.backgroundColor = 'transparent';
        break;

      case 'double':
        // 双线
        lineStyle.height = 0;
        const doubleThickness = Math.max(1, Math.floor(cfg.thickness / 3));
        lineStyle.borderTop = `${doubleThickness}px double ${cfg.color}`;
        lineStyle.backgroundColor = 'transparent';
        break;

      case 'gradient':
        // 渐变线
        lineStyle.height = cfg.thickness;
        lineStyle.background = `linear-gradient(90deg, ${cfg.gradientFrom}, ${cfg.gradientTo})`;
        lineStyle.borderRadius = cfg.thickness / 2;
        break;

      case 'wave':
        // 波浪线 - 使用SVG pattern重复
        const waveHeight = cfg.thickness * 2;
        const waveWidth = cfg.thickness * 4;
        const patternId = `wave-pattern-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        return (
          <svg 
            className="divider-wave"
            width={cfg.lengthPercent + '%'}
            height={waveHeight}
            style={{ flexShrink: 0, display: 'block' }}
            preserveAspectRatio="none"
          >
            <defs>
              <pattern id={patternId} x="0" y="0" width={waveWidth} height={waveHeight} patternUnits="userSpaceOnUse">
                <path
                  d={`M 0 ${waveHeight / 2} Q ${waveWidth / 4} 0, ${waveWidth / 2} ${waveHeight / 2} T ${waveWidth} ${waveHeight / 2}`}
                  stroke={cfg.color}
                  strokeWidth={cfg.thickness}
                  fill="none"
                />
              </pattern>
            </defs>
            <rect width="100%" height={waveHeight} fill={`url(#${patternId})`} />
          </svg>
        );

      default:
        lineStyle.height = cfg.thickness;
        lineStyle.backgroundColor = cfg.color;
        lineStyle.borderRadius = cfg.thickness / 2;
    }

    return <span className="divider-line" style={lineStyle} />;
  };

  // 渲染一行分隔线
  const renderRow = (pos) => {
    const rowStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginTop: pos === 'top' ? cfg.marginTop : 0,
      marginBottom: pos === 'bottom' ? cfg.marginBottom : 0,
    };

    // 根据对齐方式设置justifyContent
    if (cfg.align === 'center') {
      rowStyle.justifyContent = 'center';
    } else if (cfg.align === 'left') {
      rowStyle.justifyContent = 'flex-start';
    } else {
      rowStyle.justifyContent = 'flex-end';
    }

    // 根据对齐方式和样式类型组合元素
    const line = renderLine();
    const decoration = renderDecoration();

    let elements = [];
    
    if (cfg.align === 'center' && supportsDecoration) {
      // 居中：装饰-线-装饰
      elements = [decoration, line, decoration];
    } else if (cfg.align === 'left' && supportsDecoration) {
      // 靠左：装饰-线
      elements = [decoration, line];
    } else if (cfg.align === 'right' && supportsDecoration) {
      // 靠右：线-装饰
      elements = [line, decoration];
    } else {
      // 仅线条
      elements = [line];
    }

    return (
      <div className="divider-row" style={rowStyle}>
        {elements}
      </div>
    );
  };

  return (
    <div className="label-content-divider">
      {cfg.showTop && renderRow('top')}
      {cfg.showBottom && renderRow('bottom')}
    </div>
  );
}
